import { v4 as uuidv4 } from 'uuid';
import { SnowAttribute } from '@amzn/snow-nxt-workflow-service-typescript-client';
import {
	isTaskSpecificAttribute,
	isWorkflowSpecificAttribute,
} from 'src/utils/attributeTypeUtils';
import { TaskDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { TaskTypeListItem } from 'src/stores/taskTypeListStore/taskTypeListStore.interface';
import { WorkflowDefinitionStatus } from 'src/constants/commonEnums';
import { SUB_WORKFLOW_TASK_TYPE } from 'src/constants/CommonConstants';
import { deepCopy } from 'src/utils/CommonUtils';

export function createTaskDefinitionFromType(
	taskType: Pick<
		TaskTypeListItem,
		| 'taskName'
		| 'taskInputs'
		| 'taskOutputs'
		| 'averageExecutionTime'
		| 'owner'
		| 'entityTypeSupported'
		| 'operation'
		| 'operationId'
		| 'isSubWorkflow'
		| 'childWorkflowId'
	>,
	userName: string,
	entityType: string
): TaskDefinition {
	return {
		taskInputs: taskType.taskInputs,
		taskOutputs: taskType.taskOutputs,
		asynchronous: false,
		averageExecutionTime: {
			days: 0,
			hours: 0,
			minutes: 1,
			...taskType.averageExecutionTime,
		},
		comment: '',
		conditionalAttributes: [],
		createdBy: userName,
		creationTime: Math.floor(new Date().getTime() / 1000),
		entityTypeSupported: taskType.entityTypeSupported || entityType,
		owner: taskType.owner || userName,
		lastUpdateTime: 0,
		lastUpdatedBy: '',
		genericAPITaskName: '',
		regionalized: false,
		stage: '',
		latest: true,
		status: WorkflowDefinitionStatus.ACTIVE,
		taskParameter: '',
		taskDefinitionVersion: 1,
		notificationTask: false,
		individualSIMTRequired: false,
		batchExecutionRequired: false,
		taskDescription: '',
		taskId: uuidv4(),
		taskName: taskType.taskName,
		taskType: taskType.isSubWorkflow
			? SUB_WORKFLOW_TASK_TYPE
			: taskType.taskName,
		operation: taskType.operation,
		operationId: taskType.operationId,
		childWorkflowId: taskType.childWorkflowId,
	};
}




/**
 * Collects the inputs a parent must supply for an embedded sub-workflow, from the
 * child's top-level tasks.
 */
export function buildSubWorkflowTaskInputsFromChildTasks(
	childTaskDefinitions: TaskDefinition[] = []
): SnowAttribute[] {
	const childTasks = childTaskDefinitions.filter(
		(childTask) => childTask.taskType !== SUB_WORKFLOW_TASK_TYPE
	);

	const workflowSpecificInputs = new Map(
		childTasks
			.flatMap((childTask) =>
				(childTask.taskInputs ?? []).filter(isWorkflowSpecificAttribute)
			)
			.map((childTaskInput) => [
				childTaskInput.attributeName,
				deepCopy(childTaskInput),
			])
	);

	const taskSpecificInputs = childTasks.flatMap((childTask) =>
		(childTask.taskInputs ?? [])
			.filter(isTaskSpecificAttribute)
			.map((childTaskInput) => ({
				...deepCopy(childTaskInput),
				attributeName: `${childTask.taskId}.${childTaskInput.attributeName}`,
			}))
	);

	return [...workflowSpecificInputs.values(), ...taskSpecificInputs];
}

export interface SubWorkflowChildTaskGroup {
	childTaskId: string;
	childTaskName: string;
	taskInputs: SnowAttribute[];
}

export interface SubWorkflowInputGroups {
	workflowLevelInputs: SnowAttribute[];
	childTaskGroups: SubWorkflowChildTaskGroup[];
}

/**
 * Splits a SubWorkflow task's inputs into the two shapes the panel renders: the
 * workflow-level ones as a flat list, and the task-specific ones grouped by the child
 * task that owns them.
 */
export function buildSubWorkflowInputGroups(
	subWorkflowTaskInputs: SnowAttribute[] = [],
	childTaskDefinitions: TaskDefinition[] = []
): SubWorkflowInputGroups {
	const childTaskNameById = new Map(
		childTaskDefinitions.map((childTask) => [childTask.taskId, childTask.taskName])
	);
	const taskInputsByChildTaskId = new Map<string, SnowAttribute[]>();

	subWorkflowTaskInputs.filter(isTaskSpecificAttribute).forEach((taskInput) => {
		const [childTaskId] = (taskInput.attributeName ?? '').split('.');
		const existingGroup = taskInputsByChildTaskId.get(childTaskId);

		if (existingGroup) {
			existingGroup.push(taskInput);
		} else {
			taskInputsByChildTaskId.set(childTaskId, [taskInput]);
		}
	});

	return {
		workflowLevelInputs: subWorkflowTaskInputs.filter(isWorkflowSpecificAttribute),
		childTaskGroups: [...taskInputsByChildTaskId].map(
			([childTaskId, groupedTaskInputs]) => ({
				childTaskId,
				childTaskName: childTaskNameById.get(childTaskId) || childTaskId,
				taskInputs: groupedTaskInputs,
			})
		),
	};
}

export function cloneTaskDefinition(
	source: TaskDefinition,
	userName: string
): TaskDefinition {
	const now = Math.floor(new Date().getTime() / 1000);

	return {
		...deepCopy(source),
		taskId: uuidv4(),
		taskName: `${source.taskName} (Copy)`,
		comment: `Copied from ${source.taskName}`,
		createdBy: userName,
		creationTime: now,
		lastUpdatedBy: userName,
		lastUpdateTime: now,
	};
}
